import { supabase } from '../lib/supabase';
import type {
  PlayerAttendance,
  AttendanceStats,
  TeamAttendanceSummary,
  AttendanceTrendPoint,
  AtRiskPlayer,
} from '../types/playerCard';

/** Below this attendance %, a player is flagged as at-risk on the team attendance report. */
export const ATTENDANCE_AT_RISK_THRESHOLD = 70;

/**
 * Pure aggregation math shared by getAttendanceStats (per-player) and
 * getTeamAttendanceSummary (team-wide) — kept dependency-free so it's
 * easy to unit test without mocking supabase.
 *
 * Attendance rules:
 * - "Present" count (practices_present): late = present, no_show = absent, excused = absent
 * - Attendance %: only no_show and late count against it; excused is neutral
 * - Denominator is always the real total of past practices/games for the team
 */
export function computeAttendanceStats(
  records: Pick<PlayerAttendance, 'event_type' | 'status' | 'event_date'>[],
  totalPracticesCount: number,
  totalGamesCount: number
): AttendanceStats {
  // Separate practices and games
  const practices = records.filter(a => a.event_type === 'practice');
  const games = records.filter(a => a.event_type === 'game');

  // Count each exception type
  const practicesLate = practices.filter(a => a.status === 'late').length;
  const practicesNoShow = practices.filter(a => a.status === 'no_show').length;
  const practicesExcused = practices.filter(a => a.status === 'excused').length;

  const gamesLate = games.filter(a => a.status === 'late').length;
  const gamesNoShow = games.filter(a => a.status === 'no_show').length;
  const gamesExcused = games.filter(a => a.status === 'excused').length;

  // "Present" = attended at any time (including late). no_show and excused = not present.
  const practicesPresent = Math.max(0, totalPracticesCount - practicesNoShow - practicesExcused);
  const gamesPresent = Math.max(0, totalGamesCount - gamesNoShow - gamesExcused);

  // Attendance % — only late and no_show are derogatory; excused is neutral.
  // Formula: (total - derogatory) / total × 100
  const totalEvents = totalPracticesCount + totalGamesCount;
  const derogatoryExceptions = practicesLate + practicesNoShow + gamesLate + gamesNoShow;
  const attendancePercentage = totalEvents > 0
    ? Math.round(((totalEvents - derogatoryExceptions) / totalEvents) * 100)
    : 100;

  // Last attended = most recent event where the player physically showed up (present or late)
  const attendedRecords = records.filter(a => a.status !== 'no_show' && a.status !== 'excused');
  const lastAttended = attendedRecords.length > 0
    ? attendedRecords.sort((a, b) =>
        new Date(b.event_date).getTime() - new Date(a.event_date).getTime()
      )[0].event_date
    : undefined;

  return {
    total_practices: totalPracticesCount,
    total_games: totalGamesCount,
    practices_present: practicesPresent,
    practices_late: practicesLate,
    practices_no_show: practicesNoShow,
    practices_excused: practicesExcused,
    games_present: gamesPresent,
    games_late: gamesLate,
    games_no_show: gamesNoShow,
    games_excused: gamesExcused,
    attendance_percentage: attendancePercentage,
    last_attended: lastAttended,
  };
}

/**
 * Service for managing Player Attendance
 * Tracks attendance for practices and games
 */
class PlayerAttendanceService {
  /**
   * Log attendance exception for a player
   * Note: Only exceptions are tracked. If a player is present, no record is created.
   * 
   * @param status - 'late' (arrived after warmups), 'no_show' (did not attend), or 'excused' (excused absence)
   */
  async logAttendance(
    playerId: string,
    teamId: string,
    eventType: 'practice' | 'game',
    eventId: string,
    status: 'late' | 'no_show' | 'excused',
    eventDate: string,
    eventTime?: string,
    arrivalTime?: string,
    departureTime?: string,
    notes?: string
  ): Promise<PlayerAttendance> {
    const { data, error } = await supabase
      .from('player_attendance')
      .insert({
        player_id: playerId,
        team_id: teamId,
        event_type: eventType,
        event_id: eventId,
        event_date: eventDate,
        event_time: eventTime || null,
        status,
        arrival_time: arrivalTime || null,
        departure_time: departureTime || null,
        notes: notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttendanceService] Error logging attendance:', error);
      throw error;
    }

    return data;
  }

  /**
   * Remove attendance exception (mark player as present)
   * This deletes the exception record, effectively marking the player as present
   */
  async removeAttendanceException(
    playerId: string,
    eventType: 'practice' | 'game',
    eventId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('player_attendance')
      .delete()
      .eq('player_id', playerId)
      .eq('event_type', eventType)
      .eq('event_id', eventId);

    if (error) {
      console.error('[PlayerAttendanceService] Error removing attendance exception:', error);
      throw error;
    }
  }

  /**
   * Update attendance record
   */
  async updateAttendance(
    attendanceId: string,
    updates: Partial<PlayerAttendance>
  ): Promise<PlayerAttendance> {
    const { data, error } = await supabase
      .from('player_attendance')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', attendanceId)
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttendanceService] Error updating attendance:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get attendance for a specific event
   */
  async getAttendanceForEvent(
    eventType: 'practice' | 'game',
    eventId: string
  ): Promise<PlayerAttendance[]> {
    const { data, error } = await supabase
      .from('player_attendance')
      .select('*')
      .eq('event_type', eventType)
      .eq('event_id', eventId)
      .order('event_date', { ascending: true });

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching attendance:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get attendance history for a player
   */
  async getAttendanceHistory(
    playerId: string,
    teamId: string,
    startDate?: string,
    endDate?: string
  ): Promise<PlayerAttendance[]> {
    let query = supabase
      .from('player_attendance')
      .select('*')
      .eq('player_id', playerId)
      .eq('team_id', teamId)
      .order('event_date', { ascending: false });

    if (startDate) {
      query = query.gte('event_date', startDate);
    }

    if (endDate) {
      query = query.lte('event_date', endDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching attendance history:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get attendance statistics for a player
   * 
   * Attendance rules:
   * - "Present" count (practices_present): late = present, no_show = absent, excused = absent
   * - Attendance %: only no_show and late count against it; excused is neutral
   * - Denominator is always the real total of past practices/games for the team
   */
  async getAttendanceStats(
    playerId: string,
    teamId: string,
    totalPractices?: number,
    totalGames?: number
  ): Promise<AttendanceStats> {
    const today = new Date().toISOString().split('T')[0];

    // Fetch exception records and real practice/game totals in parallel
    const [attendanceResult, practicesResult, gamesResult] = await Promise.all([
      supabase
        .from('player_attendance')
        .select('*')
        .eq('player_id', playerId)
        .eq('team_id', teamId),
      // Only count past-or-today practices so future events don't inflate the denominator
      totalPractices === undefined
        ? supabase
            .from('practices')
            .select('id', { count: 'exact', head: true })
            .eq('team_id', teamId)
            .lte('date', today)
        : Promise.resolve({ count: totalPractices, error: null }),
      // Games denominator — kept for future use; defaults to 0 when not supplied
      totalGames === undefined
        ? Promise.resolve({ count: 0, error: null })
        : Promise.resolve({ count: totalGames, error: null }),
    ]);

    if (attendanceResult.error) {
      console.error('[PlayerAttendanceService] Error fetching attendance for stats:', attendanceResult.error);
      throw attendanceResult.error;
    }

    const attendance = attendanceResult.data || [];

    // Use fetched totals as denominator (fall back to exception count only as last resort)
    const totalPracticesCount = (practicesResult.count ?? null)
      ?? attendance.filter(a => a.event_type === 'practice').length;
    const totalGamesCount = (gamesResult.count ?? null)
      ?? attendance.filter(a => a.event_type === 'game').length;

    return computeAttendanceStats(attendance, totalPracticesCount, totalGamesCount);
  }

  /**
   * Team-wide attendance rollup for the Attendance Report — one pass over the
   * team's roster and attendance records (no N+1 per-player queries).
   */
  async getTeamAttendanceSummary(teamId: string): Promise<TeamAttendanceSummary> {
    const today = new Date().toISOString().split('T')[0];

    const [playerCardsResult, attendanceResult, practicesResult, gamesResult] = await Promise.all([
      // Roster players — same player_cards join used by the attendance-marking panel,
      // so this report only reflects players who are actually on the roster.
      supabase
        .from('player_cards')
        .select(`
          player_id,
          player:players!inner(id, first_name, last_name, number)
        `)
        .eq('team_id', teamId),
      supabase
        .from('player_attendance')
        .select('*')
        .eq('team_id', teamId),
      supabase
        .from('practices')
        .select('id, date', { count: 'exact' })
        .eq('team_id', teamId)
        .lte('date', today),
      supabase
        .from('games')
        .select('id, date', { count: 'exact' })
        .eq('team_id', teamId)
        .lte('date', today),
    ]);

    if (playerCardsResult.error) {
      console.error('[PlayerAttendanceService] Error fetching roster for team summary:', playerCardsResult.error);
      throw playerCardsResult.error;
    }
    if (attendanceResult.error) {
      console.error('[PlayerAttendanceService] Error fetching attendance for team summary:', attendanceResult.error);
      throw attendanceResult.error;
    }

    type RosterPlayer = { id: string; first_name: string; last_name: string; number: number | null };
    const rosterPlayers = (playerCardsResult.data || [])
      .map((card) => card.player as unknown as RosterPlayer | null)
      .filter((player, index, self): player is RosterPlayer =>
        !!player && index === self.findIndex((p) => p?.id === player.id)
      );

    const attendance: PlayerAttendance[] = attendanceResult.data || [];
    const totalPracticesCount = practicesResult.count ?? 0;
    const totalGamesCount = gamesResult.count ?? 0;

    // Per-player stats, computed from the single attendance array (no extra queries).
    const players = rosterPlayers
      .map((player) => {
        const playerRecords = attendance.filter((a) => a.player_id === player.id);
        const stats = computeAttendanceStats(playerRecords, totalPracticesCount, totalGamesCount);
        return {
          player_id: player.id,
          first_name: player.first_name,
          last_name: player.last_name,
          number: player.number,
          stats,
        };
      })
      .sort((a, b) => a.stats.attendance_percentage - b.stats.attendance_percentage);

    const atRiskPlayers: AtRiskPlayer[] = players
      .filter((p) => p.stats.attendance_percentage < ATTENDANCE_AT_RISK_THRESHOLD)
      .map((p) => ({
        player_id: p.player_id,
        first_name: p.first_name,
        last_name: p.last_name,
        number: p.number,
        attendance_percentage: p.stats.attendance_percentage,
      }));

    // Team-wide %: same derogatory-based formula, applied across every player's events combined.
    const rosterSize = rosterPlayers.length;
    const totalTeamEvents = rosterSize * (totalPracticesCount + totalGamesCount);
    const totalDerogatory = attendance.filter((a) => a.status === 'late' || a.status === 'no_show').length;
    const overallAttendancePercentage = totalTeamEvents > 0
      ? Math.round(((totalTeamEvents - totalDerogatory) / totalTeamEvents) * 100)
      : 100;

    // Trend: distinct past event dates (practices ∪ games) in chronological order,
    // each point's % = (rosterSize - exceptionsOnThatDate) / rosterSize × 100.
    const practiceDates = (practicesResult.data || []).map((p: { date: string }) => ({ date: p.date, type: 'practice' as const }));
    const gameDates = (gamesResult.data || []).map((g: { date: string }) => ({ date: g.date, type: 'game' as const }));
    const allEventDates = [...practiceDates, ...gameDates].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const trend: AttendanceTrendPoint[] = rosterSize > 0
      ? allEventDates.slice(-10).map(({ date, type }) => {
          const exceptionsThatDate = attendance.filter(
            (a) => a.event_date === date && a.event_type === type
              && (a.status === 'late' || a.status === 'no_show')
          ).length;
          return {
            event_date: date,
            event_type: type,
            attendance_percentage: Math.round(((rosterSize - exceptionsThatDate) / rosterSize) * 100),
          };
        })
      : [];

    return {
      total_practices: totalPracticesCount,
      total_games: totalGamesCount,
      overall_attendance_percentage: overallAttendancePercentage,
      players,
      trend,
      at_risk_players: atRiskPlayers,
    };
  }

  /**
   * Recent raw exception log for a team, newest first, capped so this stays
   * bounded regardless of roster/season size. Used by Coach Steve's
   * attendance agent, which does its own counting/filtering over the raw
   * rows for whatever window the coach actually asked about (e.g. "the
   * last 2 weeks") rather than us pre-computing every possible window.
   */
  async getTeamAttendanceExceptions(
    teamId: string,
    sinceDate?: string,
    limit = 150
  ): Promise<Array<{
    player_id: string;
    first_name: string;
    last_name: string;
    number: number | null;
    event_type: 'practice' | 'game';
    event_date: string;
    status: 'late' | 'no_show' | 'excused';
  }>> {
    let query = supabase
      .from('player_attendance')
      .select('player_id, event_type, event_date, status, player:players(first_name, last_name, number)')
      .eq('team_id', teamId)
      .order('event_date', { ascending: false })
      .limit(limit);

    if (sinceDate) {
      query = query.gte('event_date', sinceDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching team attendance exceptions:', error);
      throw error;
    }

    type JoinedRow = {
      player_id: string;
      event_type: 'practice' | 'game';
      event_date: string;
      status: 'late' | 'no_show' | 'excused';
      player: { first_name: string; last_name: string; number: number | null } | null;
    };

    return ((data as unknown as JoinedRow[]) || []).map((row) => ({
      player_id: row.player_id,
      first_name: row.player?.first_name ?? 'Unknown',
      last_name: row.player?.last_name ?? '',
      number: row.player?.number ?? null,
      event_type: row.event_type,
      event_date: row.event_date,
      status: row.status,
    }));
  }

  /**
   * Delete attendance record
   */
  async deleteAttendance(attendanceId: string): Promise<void> {
    const { error } = await supabase
      .from('player_attendance')
      .delete()
      .eq('id', attendanceId);

    if (error) {
      console.error('[PlayerAttendanceService] Error deleting attendance:', error);
      throw error;
    }
  }

  /**
   * Batch log attendance exceptions for multiple players
   * Note: Only exceptions should be included (late, no_show, excused)
   */
  async batchLogAttendance(
    attendanceRecords: Array<{
      player_id: string;
      team_id: string;
      event_type: 'practice' | 'game';
      event_id: string;
      status: 'late' | 'no_show' | 'excused';
      event_date: string;
      event_time?: string;
      arrival_time?: string;
      departure_time?: string;
      notes?: string;
    }>
  ): Promise<PlayerAttendance[]> {
    // Filter out empty arrays
    if (attendanceRecords.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('player_attendance')
      .insert(attendanceRecords)
      .select();

    if (error) {
      console.error('[PlayerAttendanceService] Error batch logging attendance:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Upsert attendance exception (insert or update if exists)
   * Useful for updating a player's exception status
   */
  async upsertAttendanceException(
    playerId: string,
    teamId: string,
    eventType: 'practice' | 'game',
    eventId: string,
    status: 'late' | 'no_show' | 'excused',
    eventDate: string,
    eventTime?: string,
    notes?: string
  ): Promise<PlayerAttendance> {
    // First, try to find existing record
    const { data: existing } = await supabase
      .from('player_attendance')
      .select('*')
      .eq('player_id', playerId)
      .eq('event_type', eventType)
      .eq('event_id', eventId)
      .single();

    if (existing) {
      // Update existing record
      return this.updateAttendance(existing.id, {
        status,
        notes: notes || null,
        updated_at: new Date().toISOString(),
      });
    } else {
      // Create new record
      return this.logAttendance(
        playerId,
        teamId,
        eventType,
        eventId,
        status,
        eventDate,
        eventTime,
        undefined,
        undefined,
        notes
      );
    }
  }
}

export const playerAttendanceService = new PlayerAttendanceService();

